import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { flatApi } from './api';

// ─── Icons ───
const IconArrowLeft = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>);
const IconTrash = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>);
const IconEdit = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const IconPlus = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);

const LABEL_MAP = {
    FLAT: 'Flat', ROOM: 'Room', PG: 'PG', ANY: 'Any',
    MALE: 'Male', FEMALE: 'Female',
    FURNISHED: 'Furnished', SEMI: 'Semi-Furnished', UNFURNISHED: 'Unfurnished',
    SINGLE: 'Single', SHARED: 'Shared',
};

const MyRequirementPage = ({ onBack, onEditRequirement, onPostNew }) => {
    const { tokens, refreshAccessToken } = useAuth();
    const [requirement, setRequirement] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRequirement = async () => {
        setLoading(true);
        try {
            const res = await flatApi.getRequirements(tokens.access);
            setRequirement(res);
        } catch (err) {
            console.log('No requirement found');
            setRequirement(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRequirement(); }, []);

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete your requirement?')) return;
        try {
            setLoading(true);
            await flatApi.deleteRequirement(tokens.access);
            setRequirement(null);
            await refreshAccessToken();
        } catch (err) {
            alert('Failed to delete requirement');
        } finally {
            setLoading(false);
        }
    };

    const formatBudget = (min, max) => {
        if (!min && !max) return 'Not set';
        return `₹${(min || 0).toLocaleString('en-IN')} - ₹${(max || 0).toLocaleString('en-IN')}`;
    };

    const InfoItem = ({ icon, label, value, color = '#5B7F2C' }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 0', borderBottom: '1px solid #E5E9E3' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                {icon}
            </div>
            <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1F2937', marginTop: '2px' }}>{value || 'Not set'}</div>
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #5B7F2C 0%, #A6C63D 100%)', padding: '24px', color: 'white' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                        <IconArrowLeft />
                    </button>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', flex: 1, color: 'white' }}>My Requirement</h2>
                </div>
            </div>

            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 24px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: '#9CA3AF', fontSize: '18px', fontWeight: '600' }}>Loading...</div>
                ) : !requirement ? (
                    <div style={{ textAlign: 'center', padding: '80px 40px', background: 'white', borderRadius: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#F4F8D8', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>📣</div>
                        <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', margin: '0 0 8px' }}>No requirement posted</h3>
                        <p style={{ color: '#6B7280', margin: '0 0 24px', fontSize: '15px' }}>Let others know what you're looking for</p>
                        <button onClick={onPostNew} style={{ background: 'linear-gradient(135deg, #5B7F2C, #A6C63D)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px 32px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', boxShadow: '0 8px 24px rgba(91, 127, 44, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <IconPlus size={18} /> Post Requirement
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Main Card */}
                        <div style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                            {/* Badge header */}
                            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E9E3' }}>
                                <span style={{ background: 'linear-gradient(135deg, #5B7F2C, #A6C63D)', color: 'white', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    🔍 Looking for {LABEL_MAP[requirement.postType] || 'Any'}
                                </span>
                            </div>

                            {/* Info grid */}
                            <div style={{ padding: '8px 24px' }}>
                                <InfoItem icon="💰" label="Budget" value={formatBudget(requirement.budgetMin, requirement.budgetMax)} color="#5B7F2C" />
                                <InfoItem icon="📍" label="Preferred Location" value={requirement.location?.name} color="#3B82F6" />
                                <InfoItem icon="👤" label="Gender Preference" value={LABEL_MAP[requirement.gender] || 'Any'} color="#8B5CF6" />
                                <InfoItem icon="👥" label="Occupancy" value={LABEL_MAP[requirement.occupany] || 'Any'} color="#10B981" />
                                <InfoItem icon="🛏️" label="Furnishing" value={LABEL_MAP[requirement.furnishing] || 'Any'} color="#EC4899" />
                                {requirement.isHideMobileNumber && (
                                    <InfoItem icon="🙈" label="Phone Visibility" value="Hidden" color="#6B7280" />
                                )}
                            </div>

                            {requirement.description && (
                                <div style={{ padding: '20px 24px', borderTop: '1px solid #E5E9E3' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px' }}>Additional Details</div>
                                    <p style={{ color: '#4B5563', lineHeight: '1.7', margin: 0, fontSize: '15px' }}>{requirement.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <button onClick={() => onEditRequirement(requirement)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'linear-gradient(135deg, #5B7F2C, #A6C63D)', color: 'white', border: 'none', borderRadius: '16px', padding: '16px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', boxShadow: '0 8px 24px rgba(91, 127, 44, 0.25)' }}>
                                <IconEdit size={18} /> Update
                            </button>
                            <button onClick={handleDelete} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '16px', padding: '16px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', boxShadow: '0 8px 24px rgba(239, 68, 68, 0.25)' }}>
                                <IconTrash size={18} /> Delete
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyRequirementPage;
